import { Flex, Modal, Select, Typography } from 'antd';
import { useMemo, useState } from 'react';

import { useAdminUserController_getUsers } from '@api-client/generated/AdminUserController/getUsers';
import { Schemas } from '@api-client/generated/types';
import { emptyAccessRights, ROLE_ACCESS_DEFAULTS } from '@constants';
import { fullName } from '@utils';

import AccessRightsGrid from './AccessRightsGrid';
import * as S from './styled';

type AccessRights = Schemas.AccessRights;

type Props = {
  open: boolean;
  attachedUserIds: string[];
  isAttaching: boolean;
  onClose: () => void;
  onAttach: (userId: string, accessRights: AccessRights) => void;
};

/** Prefill rights from the selected employee's backoffice role; fall back to no
 * access for employees without one. */
const defaultsForRole = (
  adminRole: Schemas.User['adminRole'] | null | undefined
): AccessRights =>
  adminRole ? { ...ROLE_ACCESS_DEFAULTS[adminRole] } : emptyAccessRights();

const AttachEmployeeModal = ({
  open,
  attachedUserIds,
  isAttaching,
  onClose,
  onAttach,
}: Props) => {
  const { data: users, isPending } = useAdminUserController_getUsers({
    params: {},
  });

  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [rights, setRights] = useState<AccessRights>(emptyAccessRights);

  const attachedIds = useMemo(
    () => new Set(attachedUserIds),
    [attachedUserIds]
  );

  // Only backoffice employees (those with a role) who are not already members.
  const options = useMemo(
    () =>
      (users ?? [])
        .filter((user) => user.adminRole && !attachedIds.has(user.id))
        .map((user) => ({
          label: `${fullName(user)} · ${user.email}`,
          value: user.id,
        })),
    [users, attachedIds]
  );

  const reset = () => {
    setSelectedUserId(undefined);
    setRights(emptyAccessRights());
  };

  const handleSelect = (userId: string) => {
    setSelectedUserId(userId);
    const employee = users?.find((user) => user.id === userId);
    setRights(defaultsForRole(employee?.adminRole));
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedUserId) return;
    onAttach(selectedUserId, rights);
  };

  return (
    <Modal
      title="Add team member"
      open={open}
      onCancel={handleClose}
      onOk={handleConfirm}
      okText="Attach employee"
      confirmLoading={isAttaching}
      okButtonProps={{ disabled: !selectedUserId }}
      afterClose={reset}
      destroyOnClose
    >
      <Flex gap={20} vertical>
        <Flex gap={6} vertical>
          <Typography.Text strong>Employee</Typography.Text>
          <Select
            showSearch
            value={selectedUserId}
            placeholder="Select an employee"
            loading={isPending}
            options={options}
            onChange={handleSelect}
            optionFilterProp="label"
            notFoundContent={
              isPending ? 'Loading…' : 'No employees available to add'
            }
          />
        </Flex>

        <Flex gap={6} vertical>
          <Typography.Text strong>Access rights</Typography.Text>
          <S.ModalHint>
            Prefilled from the employee’s role. Adjust before attaching.
          </S.ModalHint>
          <AccessRightsGrid
            value={rights}
            onChange={setRights}
            disabled={!selectedUserId}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default AttachEmployeeModal;
